import { ATTRIBUTES, DOM_EVENTS } from "@js/constants/html-constants.es6.js"
import {KEYS} from "@js/constants/js-constants.es6.js";
import Element from "@js/core/element.es6.js";
import Paragraph from "@js/core/paragraph.es6.js";
import OverlayService from "@js/overlay-service.es6.js";
import * as focusTrap from "focus-trap";

/**
 * A base class for paragraphs that should be displayed as modals.  When the
 * modal of interest is loaded, the initialize method should be called to set
 * up generic modal functionality.  This should cover accessibility
 * considerations for modals.
 *
 * This includes:
 * - opening the modal (and showing the modal overlay)
 * - closing the modal (and hiding the modal overlay)
 * - restricting focus to within the modal
 * - closing the modal upon hitting 'Escape' (if allowed)
 * - closing the modal upon clicking the close button (if allowed)
 * - closing the modal upon focusing on close button and hitting 'Enter'
 *   (if allowed)
 */
export default class ModalParagraph extends Paragraph {
  #overlayService;

  #$originalParent;

  #allowClose;

  #focusTrap;

  constructor({ machineName, parentElement }) {
    super({
      machineName,
      parentElement,
    });

    this.#overlayService = new OverlayService();

    /**
     * Store the parent of this paragraph element as it is initially rendered
     * by Drupal so that we can put the modal back in its original location
     * within the DOM when closed.
     */
    this.#$originalParent = this.$().parent();

    this.#allowClose = this.closeButton.$().length > 0;
  }

  /**
   * This element is not within the modal itself.  It is the placeholder
   * wrapper for modals, which is added to the page through
   * docroot/themes/custom/luminis/templates/layout/page.html.twig.
   */
  static get modalWrapper() {
    return new Element({
      selector: '.desktop-overlay + .modal-wrapper',
    });
  }

  /**
   * This property should return the modal, wherever it currently exists in the
   * DOM.
   */
  get activeModal() {
    const modalMoved = ModalParagraph.modalWrapper.$().children().length > 0;

    if (modalMoved) {
      return new Element({
        selector: '.modal',
        parentElement: ModalParagraph.modalWrapper,
      });
    }

    return this;
  }

  get closeButton() {
    return new Element({
      selector: '.close-button',
      parentElement: this.activeModal,
    });
  }

  open() {
    this.#showOverlay();
    this.#showModal();
    this.#restrictFocusToWithinModal();

    if (this.#allowClose) {
      this.#updateCloseButton();
      this.#closeOnEscapeKeypress();
    }
  }

  #showOverlay() {
    const overlayClickHandler = () => {
      if (this.#allowClose) {
        this.close();
      }
    };

    this.#overlayService.toggleOverlay(true, overlayClickHandler, true);
  }

  /**
   * Show the modal by moving it from its initial Drupal-rendered location to
   * the modal wrapper element at the end of the DOM and removing the hidden
   * attribute.
   *
   * Moving it to the end of the DOM uses stacking order to keep
   * the modal in front of the modal overlay and other elements.  It also
   * allows the modal to be rendered inside of any Drupal element, unaffected
   * by the z-index applied to its parent elements.
   */
  #showModal() {
    const $this = this.$();
    ModalParagraph.modalWrapper.$().empty().append($this);

    $this.removeAttr(ATTRIBUTES.HIDDEN);
  }

  #restrictFocusToWithinModal() {
    const modalElement = this.activeModal.$()[0];

    this.#focusTrap = focusTrap.createFocusTrap(modalElement, {
      initialFocus: modalElement,
      escapeDeactivates: this.#allowClose,
      clickOutsideDeactivates: this.#allowClose,
      allowOutsideClick: this.#allowClose,
    });

    this.#focusTrap.activate();
  }

  #updateCloseButton() {
    const $closeButton = this.closeButton.$();
    $closeButton.off();

    $closeButton.on(DOM_EVENTS.CLICK, event => {
      event.preventDefault();
      this.close();
    });

    $closeButton.on(DOM_EVENTS.KEYDOWN, async event => {
      if (event.key === KEYS.ENTER) {
        event.preventDefault();
        this.close();
      }
    });
  }

  #closeOnEscapeKeypress() {
    const $modalWrapper = this.activeModal.$();
    $modalWrapper.off();

    $modalWrapper.on(DOM_EVENTS.KEYDOWN, event => {
      if (event.key === KEYS.ESCAPE) {
        this.close();
      }
    });
  }

  close() {
    this.#hideOverlay();
    this.#hideModal();
    this.#releaseFocusToOutsideModal();
  }

  #hideOverlay() {
    this.#overlayService.toggleOverlay(false);
  }

  /**
   * Hide the modal by moving it from the modal wrapper element at the end of
   * the DOM back to its initial Drupal-rendered location and applying the
   * hidden attribute.
   */
  #hideModal() {
    const $activeModal = this.activeModal.$();
    $activeModal.attr(ATTRIBUTES.HIDDEN, true);

    this.#$originalParent.empty().append($activeModal);
  }

  #releaseFocusToOutsideModal() {
    this.#focusTrap.deactivate();
  }
}
